import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
} from '@mui/material';
import { useDraggable } from '@dnd-kit/core';
import { makeStyles } from '@mui/styles';
import { i18next } from '@/config/i18n';

type Address = {
  street?: string;
  house_number?: string;
  city?: string;
  postal_code?: string;
  country?: string;
};

type Shipment = {
  custom_id?: string;
  customer_name?: string;
  location?: { address?: Address; coordinates?: any };
  type?: 'pickup' | 'delivery' | string;
};

export type AdhocStop = {
  id: string | number;
  type: string;
  location: { coordinates: { latitude: number; longitude: number } };
  shipments_to_load?: string[];
  shipments_to_unload?: string[];
  label?: string;
  tourId?: string;
  stopIndex?: number;
  showOnMap?: boolean;
  includeInETA?: boolean;
  includeInCapacity?: boolean;
  isPending?: boolean;
};

type Props = {
  shipments: Shipment[];
  adhocStops: AdhocStop[];
  selectedId?: string | number | null;
  onPick: (s: AdhocStop) => void;
  classNames?: { header?: string; card?: string };
};

const useStyles = makeStyles({
  container: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    overflow: 'hidden',
  },
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: '#f6f7f9',
    color: '#000000',
    padding: '8px 12px',
    fontWeight: 600,
    fontSize: 14,
    borderBottom: '1px solid #e0e0e0',
  },
  list: {
    padding: 8,
  },
  card: {
    marginBottom: 8,
    borderRadius: 10,
    border: '1px solid rgba(0,0,0,0.08)',
    backgroundColor: '#fafafa',
    transition: 'box-shadow .15s ease, border-color .15s ease, background .15s ease',
    '&:hover': {
      boxShadow: '0px 2px 6px rgba(0,0,0,0.1)',
      borderColor: 'rgba(0,0,0,0.18)',
      backgroundColor: '#fff',
    },
  },
  cardSelected: {
    border: '1px solid #80c9ff',
    backgroundColor: '#eaf6ff',
    '&:hover': {
      borderColor: '#57b5ff',
      backgroundColor: '#e3f2ff',
    },
  },
  cardDragging: {
    boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
    opacity: 0.85,
  },
  cardContent: {
    padding: '10px 12px',
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1.2,
    marginBottom: 2,
    color: '#222',
  },
  subtitle: {
    fontSize: 12,
    color: '#666',
  },
});

const getAddressString = (addr: Address = {}) => {
  const parts: string[] = [];
  if (addr.street) parts.push(addr.street);
  if (addr.house_number) parts.push(addr.house_number);
  if (addr.city) parts.push(addr.city);
  return parts.filter(Boolean).join(', ');
};

// ---------- draggable card
type DraggableCardProps = {
  adhoc: AdhocStop;
  isSelected: boolean;
  className?: string;
  onClick?: () => void;
  children?: React.ReactNode;
};

const DraggableCard: React.FC<DraggableCardProps> = ({
  adhoc,
  isSelected,
  className,
  onClick,
  children,
}) => {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: String(adhoc.id),
    data: { type: 'PENDING_ADHOC', adhocId: String(adhoc.id) },
  });

  const style: React.CSSProperties = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    cursor: 'grab',
  };

  return (
    <Card
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={[
        classes.card,
        isSelected ? classes.cardSelected : '',
        isDragging ? classes.cardDragging : '',
        className,
      ].join(' ')}
      onClick={(e) => {
        if (isDragging) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onClick?.();
      }}
      style={style}
    >
      {children}
    </Card>
  );
};

// ---------- main list
const PendingShipmentsList: React.FC<Props> = ({
  shipments,
  adhocStops,
  selectedId,
  onPick,
  classNames = {},
}) => {
  const classes = useStyles();
  if (!shipments?.length) return null;

  return (
    <Box className={classes.container}>
      <Typography variant="subtitle2" className={`${classes.header} ${classNames.header || ''}`}>
         {i18next.t('common:PendingShipments.PendingTitle')}
      </Typography>

      <Box className={classes.list}>
        {shipments.map((shipment, idx) => {
          const adhocId = `pending-${shipment.custom_id || idx}`;
          const found = adhocStops.find((s) => String(s.id) === String(adhocId));

          const cardInner = (
            <CardContent className={classes.cardContent}>
              <Typography className={classes.title}>
                {shipment.customer_name || '--'}
              </Typography>
              <Typography className={classes.subtitle}>
                {getAddressString(shipment.location?.address)}
              </Typography>
            </CardContent>
          );

          if (!found) {
            return (
              <Card key={shipment.custom_id || idx} className={classes.card}>
                {cardInner}
              </Card>
            );
          }

          const isSelected = String(selectedId ?? '') === String(found.id);
          return (
            <DraggableCard
              key={shipment.custom_id || idx}
              adhoc={found}
              isSelected={isSelected}
              className={classNames.card}
              onClick={() => onPick(found)}
            >
              {cardInner}
            </DraggableCard>
          );
        })}
      </Box>
      <Divider />
    </Box>
  );
};

export default PendingShipmentsList;
